/**
 * Utility functions for parsing the new consistent API response format
 * Replaces the legacy apiResponseParser.js with TypeScript support
 */

import { ApiResponse, LeaderCloudError } from '../types/apiTypes';

/**
 * Parse and validate a consistent API response
 * @param response - The axios response object
 * @param serviceName - Name of the service for logging
 * @param operation - Name of the operation for logging
 * @returns Parsed API response
 * @throws LeaderCloudError if the response indicates failure
 */
export function parseConsistentApiResponse<T>(
  response: any,
  serviceName: string = 'unknown',
  operation: string = 'unknown'
): ApiResponse<T> {
  if (process.env.NODE_ENV === 'development' && response.status >= 400) {
    console.log(`${serviceName}: ${operation} - API response status:`, response.status);
  }

  let responseData = response.data;

  // Handle legacy body field with JSON string (backward compatibility)
  if (responseData && responseData.body && typeof responseData.body === 'string') {
    try {
      responseData = JSON.parse(responseData.body);
      if (process.env.NODE_ENV === 'development') {
        console.log(`${serviceName}: ${operation} - Parsed legacy body field:`, responseData);
      }
    } catch (parseError) {
      console.error(`${serviceName}: ${operation} - Error parsing legacy body JSON:`, parseError);
      throw new LeaderCloudError('Invalid response format from server');
    }
  }

  // Validate consistent response format
  if (!isConsistentApiResponse(responseData)) {
    console.error(`${serviceName}: ${operation} - Response does not match consistent format:`, responseData);
    throw new LeaderCloudError('Response does not match expected API format');
  }

  const apiResponse = responseData as ApiResponse<T>;

  if (process.env.NODE_ENV === 'development' && !apiResponse.success) {
    console.log(`${serviceName}: ${operation} - API error:`, apiResponse.message);
  }

  // Throw error if response indicates failure
  if (!apiResponse.success) {
    throw new LeaderCloudError(
      apiResponse.message,
      apiResponse.error,
      response.status
    );
  }

  return apiResponse;
}

/**
 * Type guard to check if response matches consistent API format
 */
function isConsistentApiResponse(data: any): data is ApiResponse<any> {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.success === 'boolean' &&
    typeof data.message === 'string' &&
    typeof data.timestamp === 'string' &&
    data.hasOwnProperty('data') &&
    data.hasOwnProperty('pagination')
  );
}

/**
 * Extract data from a successful API response
 * @param response - The parsed API response
 * @returns The data field from the response
 * @throws LeaderCloudError if response is not successful
 */
export function extractResponseData<T>(response: ApiResponse<T>): T {
  if (!response.success || response.data === null) {
    throw new LeaderCloudError(
      response.message,
      response.error
    );
  }
  return response.data;
}

/**
 * Handle API errors consistently across the application
 * @param error - The error object (could be LeaderCloudError or generic Error)
 * @param serviceName - Name of the service for logging
 * @param operation - Name of the operation for logging
 * @returns Formatted error message for UI display
 */
export function handleApiError(
  error: any,
  serviceName: string = 'unknown',
  operation: string = 'unknown'
): string {
  console.error(`${serviceName}: ${operation} - API Error:`, error);

  if (error instanceof LeaderCloudError) {
    // Handle specific error codes
    switch (error.errorInfo?.code) {
      case 'UNAUTHORIZED':
        return 'Authentication required. Please log in again.';
      case 'FORBIDDEN':
        return 'You do not have permission to perform this action.';
      case 'VALIDATION_ERROR':
        return `Validation failed: ${error.message}`;
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'RATE_LIMIT_EXCEEDED':
        return 'Too many requests. Please try again later.';
      case 'DATABASE_ERROR':
        return 'A server error occurred. Please try again later.';
      default:
        return error.message;
    }
  }

  // Handle axios errors
  if (error.response) {
    const status = error.response.status;
    switch (status) {
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'A server error occurred. Please try again later.';
      default:
        return `Server error (${status}): ${error.response.data?.message || error.message}`;
    }
  }

  // Handle network errors
  if (error.request) {
    return 'Network error. Please check your connection and try again.';
  }

  // Generic error fallback
  return error.message || 'An unexpected error occurred.';
}

/**
 * Legacy compatibility function - gradually migrate away from this
 * @deprecated Use parseConsistentApiResponse instead
 */
export function parseApiResponse(response: any, serviceName: string, operation: string): any {
  console.warn(`${serviceName}: ${operation} - Using deprecated parseApiResponse. Migrate to parseConsistentApiResponse.`);

  try {
    const consistentResponse = parseConsistentApiResponse(response, serviceName, operation);
    return consistentResponse.data;
  } catch (error) {
    if (error instanceof LeaderCloudError) {
      // For backward compatibility, return the original response data if parsing fails
      return response.data;
    }
    throw error;
  }
}

/**
 * Legacy compatibility function - gradually migrate away from this
 * @deprecated Use extractResponseData instead
 */
export function extractArrayFromResponse(responseData: any, arrayName: string, serviceName: string): any[] {
  console.warn(`${serviceName}: Using deprecated extractArrayFromResponse. Migrate to consistent API format.`);

  // If responseData is already a consistent API response, extract the data
  if (isConsistentApiResponse(responseData)) {
    const data = responseData.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data[arrayName])) {
      return data[arrayName];
    }
  }

  // Legacy fallback logic
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (responseData && Array.isArray(responseData[arrayName])) {
    return responseData[arrayName];
  }

  if (responseData && responseData.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }

  return [];
}

/**
 * Legacy compatibility function - gradually migrate away from this
 * @deprecated Use extractResponseData instead
 */
export function extractObjectFromResponse(responseData: any, objectName: string, serviceName: string): any {
  console.warn(`${serviceName}: Using deprecated extractObjectFromResponse. Migrate to consistent API format.`);

  // If responseData is already a consistent API response, extract the data
  if (isConsistentApiResponse(responseData)) {
    const data = responseData.data;
    if (data && typeof data === 'object') {
      return data[objectName] || data;
    }
  }

  // Legacy fallback logic
  if (responseData && responseData[objectName]) {
    return responseData[objectName];
  }

  if (responseData && responseData.data) {
    return responseData.data[objectName] || responseData.data;
  }

  return responseData;
}
